import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { HOME } from "@/constants/testIds";

function CountUp({ end, suffix = "", prefix = "", duration = 1.8 }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.6 });
    const [val, setVal] = useState(0);

    useEffect(() => {
        if (!inView) return;
        let raf;
        const start = performance.now();
        const step = (now) => {
            const p = Math.min(1, (now - start) / (duration * 1000));
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(Math.round(end * eased));
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [inView, end, duration]);

    return (
        <span ref={ref}>
            {prefix}
            {val}
            <span className="text-brand">{suffix}</span>
        </span>
    );
}

const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};
const item = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export default function Hero() {
    return (
        <section className="relative pt-40 sm:pt-48 pb-24 sm:pb-32 overflow-hidden">
            <div className="pointer-events-none absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-brand/20 blur-[160px]" />
            <div className="pointer-events-none absolute top-40 -left-40 w-[400px] h-[400px] rounded-full bg-brand/10 blur-[140px]" />

            {/* Grid overlay for tech feel */}
            <div
                className="pointer-events-none absolute inset-0 opacity-[0.04]"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
                    backgroundSize: "64px 64px",
                    maskImage: "radial-gradient(ellipse at 50% 30%, black 30%, transparent 70%)",
                    WebkitMaskImage: "radial-gradient(ellipse at 50% 30%, black 30%, transparent 70%)",
                }}
            />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                <motion.div
                    className="max-w-6xl"
                    variants={container}
                    initial="hidden"
                    animate="show"
                >
                    <motion.div variants={item} className="eyebrow mb-8 flex items-center gap-3">
                        <motion.span
                            className="inline-block h-px bg-brand"
                            initial={{ width: 0 }}
                            animate={{ width: 32 }}
                            transition={{ duration: 0.9, delay: 0.1 }}
                        />
                        <span>Real Estate Marketing &middot; Dubai</span>
                    </motion.div>

                    <h1 className="font-display font-semibold tracking-[-0.05em] text-5xl sm:text-6xl lg:text-8xl leading-[0.95] text-white">
                        <motion.span className="block" variants={item}>
                            A brand that <span className="text-white/45">feels real.</span>
                        </motion.span>
                        <motion.span className="block" variants={item}>
                            A presence that{" "}
                            <span className="relative inline-block">
                                <span className="text-brand">closes deals.</span>
                                <motion.span
                                    aria-hidden
                                    className="absolute -bottom-2 left-0 right-0 h-[6px] bg-brand/40"
                                    initial={{ scaleX: 0 }}
                                    animate={{ scaleX: 1 }}
                                    style={{ transformOrigin: "left" }}
                                    transition={{ duration: 1.1, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
                                />
                            </span>
                        </motion.span>
                    </h1>

                    <motion.p variants={item} className="mt-10 text-lg sm:text-xl text-white/70 max-w-2xl leading-relaxed">
                        Marketing built for Dubai real estate agents. We craft brand identity, presence and pipeline &mdash; not follower counts.
                    </motion.p>

                    <motion.div variants={item} className="mt-10 flex flex-col sm:flex-row gap-4">
                        <a
                            href="#packages"
                            data-testid={HOME.heroCtaPrimary}
                            className="group inline-flex items-center justify-between gap-3 bg-brand hover:bg-brand-hover text-white font-semibold px-7 py-4 transition-colors relative overflow-hidden"
                        >
                            <span className="relative z-10">See Packages</span>
                            <ArrowUpRight className="relative z-10 w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            <span className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                        </a>
                        <a
                            href="#book"
                            data-testid={HOME.heroCtaWhatsapp}
                            className="group inline-flex items-center justify-between gap-3 border border-white/25 hover:border-brand text-white font-semibold px-7 py-4 transition-colors"
                        >
                            <MessageCircle className="w-5 h-5" />
                            <span>Book strategy call</span>
                        </a>
                    </motion.div>

                    <motion.div
                        variants={item}
                        className="mt-20 grid grid-cols-3 gap-6 max-w-2xl border-t border-white/10 pt-10"
                    >
                        <div>
                            <div className="numeric-display font-medium text-4xl sm:text-5xl text-white">
                                <CountUp end={9} suffix="+" />
                            </div>
                            <div className="text-[11px] text-white/45 uppercase tracking-[0.2em] mt-2 font-mono">Years in the game</div>
                        </div>
                        <div>
                            <div className="numeric-display font-medium text-4xl sm:text-5xl text-white">
                                <CountUp end={50} suffix="+" />
                            </div>
                            <div className="text-[11px] text-white/45 uppercase tracking-[0.2em] mt-2 font-mono">Realtors positioned</div>
                        </div>
                        <div>
                            <div className="numeric-display font-medium text-4xl sm:text-5xl text-white">
                                <CountUp end={14} prefix="AED " />
                            </div>
                            <div className="text-[11px] text-white/45 uppercase tracking-[0.2em] mt-2 font-mono">Lowest CPL delivered</div>
                        </div>
                    </motion.div>
                </motion.div>
            </div>

            {/* Scroll cue */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 0.8 }}
                className="hidden lg:flex absolute bottom-8 right-8 flex-col items-center gap-3 text-white/40"
            >
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] [writing-mode:vertical-rl]">scroll</span>
                <motion.span
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                    className="w-px h-8 bg-brand"
                />
            </motion.div>
        </section>
    );
}
